/*
 * Copyright © 2020-2025 XCamera. All Rights Reserved.
 * @Description: 识别中动画组件
 * @LastEditors: yingongjing
 * @LastEditTime: 2025-08-15 19:13:44
 */

import React, { useMemo } from 'react'
import { Typography } from 'antd'
import './RecognizingAnimation.scss'

const { Text } = Typography

/**
 * 识别中动画组件属性接口
 */
interface XCRecognizingAnimationProps {
  /** 显示的文字内容 */
  text?: string
  /** 自定义类名 */
  className?: string
  /** 自定义样式 */
  style?: React.CSSProperties
}

/**
 * 识别中动画组件
 * 提供未来感的渐变背景动画效果，支持自定义显示文字
 * 每个实例都有随机的动画延迟和时长，确保多个实例的动效不一致
 *
 * @param text - 显示的文字内容，默认为"识别中"
 * @param className - 自定义类名
 * @param style - 自定义样式
 */
const RecognizingAnimation: React.FC<XCRecognizingAnimationProps> = ({
  text = '识别中',
  className = '',
  style = {}
}) => {
  // 为每个实例生成随机的动画参数
  const animationParams = useMemo(() => {
    return {
      // 主脉冲动画：1.5-6s 随机时长（增大范围）
      pulseDuration: 1.5 + Math.random() * 4.5,
      // 主脉冲延迟：0-3s 随机延迟（增大范围）
      pulseDelay: Math.random() * 3,
      // 主光波动画：2-8s 随机时长（增大范围）
      rippleDuration: 2 + Math.random() * 6,
      // 主光波延迟：0-4s 随机延迟（增大范围）
      rippleDelay: Math.random() * 4,
      // 次要光效动画：2.5-9s 随机时长（增大范围）
      waveDuration: 2.5 + Math.random() * 6.5,
      // 次要光效延迟：0-5s 随机延迟（增大范围）
      waveDelay: Math.random() * 5,
      // 文字发光动画：1-5s 随机时长（增大范围）
      textGlowDuration: 1 + Math.random() * 4,
      // 文字发光延迟：0-3s 随机延迟（增大范围）
      textGlowDelay: Math.random() * 3,
      // 动画方向：随机选择正向或反向
      animationDirection: Math.random() > 0.5 ? 'normal' : 'reverse',
      // 动画速度曲线：随机选择不同的缓动函数
      timingFunction: ['ease-in', 'ease-out', 'ease-in-out', 'linear'][
        Math.floor(Math.random() * 4)
      ],
      // 透明度变化：0.8-1.0 随机透明度
      opacity: 0.8 + Math.random() * 0.2
    }
  }, [])

  // 生成随机动画样式
  const randomAnimationStyle = useMemo(() => {
    return {
      '--pulse-duration': `${animationParams.pulseDuration}s`,
      '--pulse-delay': `${animationParams.pulseDelay}s`,
      '--ripple-duration': `${animationParams.rippleDuration}s`,
      '--ripple-delay': `${animationParams.rippleDelay}s`,
      '--wave-duration': `${animationParams.waveDuration}s`,
      '--wave-delay': `${animationParams.waveDelay}s`,
      '--text-glow-duration': `${animationParams.textGlowDuration}s`,
      '--text-glow-delay': `${animationParams.textGlowDelay}s`,
      '--animation-direction': animationParams.animationDirection,
      '--timing-function': animationParams.timingFunction,
      opacity: animationParams.opacity
    } as React.CSSProperties
  }, [animationParams])

  return (
    <div
      className={`recognizing-animation ${className}`}
      style={{ ...randomAnimationStyle, ...style }}
    >
      <div className="recognizing-content">
        <Text className="recognizing-text">{text}</Text>
      </div>
    </div>
  )
}

export default RecognizingAnimation
